import { useEffect, useRef, useState } from 'react';
import { Send, MessageSquare } from 'lucide-react';

import { cn } from '@/lib/cn';

export interface ThreadMessage {
  id: string;
  sender_id: string;
  body: string;
}

interface Props {
  messages: ThreadMessage[];
  self: string;
  onSend: (body: string) => void;
  sending: boolean;
  loading?: boolean;
  emptyText?: string;
  initialDraft?: string;
  /** Disables the composer input and send button (e.g. student preview mode). */
  disabled?: boolean;
}

export function MessageThread({ messages, self, onSend, sending, loading, emptyText, initialDraft, disabled = false }: Props) {
  const [draft, setDraft] = useState(initialDraft ?? '');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const submit = () => {
    const b = draft.trim();
    if (!b || sending || disabled) return;
    onSend(b);
    setDraft('');
  };

  return (
    <div className="flex h-[420px] flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto px-1 py-2">
        {loading ? (
          <p className="py-10 text-center text-sm text-[#9ca3af]">Loading…</p>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <MessageSquare className="h-8 w-8 text-[#9ca3af]" />
            <p className="text-sm text-[#6b7280]">{emptyText ?? 'No messages yet. Start the conversation.'}</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === self;
            return (
              <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[75%] rounded-2xl px-3.5 py-2 text-sm',
                    mine ? 'bg-[#ff5a1f] text-white' : 'bg-[#f3f4f6] text-[#111827]',
                  )}
                >
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-2 flex items-end gap-2 border-t border-[#ececec] pt-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          rows={2}
          disabled={disabled}
          placeholder="Write a message… (Enter to send, Shift+Enter for a new line)"
          className="flex-1 resize-none rounded-xl border border-[#ececec] bg-white p-2.5 text-sm text-[#111827] placeholder:text-[#9ca3af] focus:border-[#ff5a1f] focus:outline-none"
        />
        <button
          type="button"
          onClick={submit}
          disabled={sending || !draft.trim() || disabled}
          title={disabled ? 'Not available in student preview' : undefined}
          className="flex h-10 items-center gap-1.5 rounded-xl bg-[#ff5a1f] px-4 text-sm font-semibold text-white disabled:opacity-40"
        >
          <Send className="h-4 w-4" /> {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
